// ============================================================
// SALT — Create Match Page
// Form for team captains to create match challenges.
// Route: /matches/create (game subdomain only, auth + captain)
// ============================================================

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import axiosClient from '../api/axiosClient';

// Components
import SeasonHeader from '../components/dashboard/SeasonHeader';
import Button from '../components/ui/Button';
import ToastContainer from '../components/ui/Toast';
import DateTimePicker from '../components/ui/DateTimePicker';
import { DefaultTeamIconSmall } from '../components/dashboard/DashboardHelpers';

// Styles
import '../styles/create-match.css';

// -- Types ---------------------------------------------------

type MatchType = 'available_now' | 'scheduled' | 'direct_challenge';
type MatchFormat = 'bo1' | 'bo3' | 'bo5' | 'bo7';

interface UserTeamInfo {
  teamId: number;
  role: 'captain' | 'member';
  teamUrl: string;
}

interface AutocompleteTeam {
  teamId: number;
  teamName: string;
  teamTag: string | null;
  logoUrl: string | null;
  rank: number | null;
  points: number;
}

interface CreateMatchPayload {
  teamId: number;
  matchType: MatchType;
  matchFormat: MatchFormat;
  scheduledAt?: string;
  challengedTeamId?: number;
}

interface ApiValidationError {
  field: string;
  message: string;
}

interface MatchLimits {
  canCreateInstant: boolean;
  canCreateScheduled: boolean;
  pendingInstantCount: number;
  pendingScheduledCount: number;
  pendingTotalCount: number;
  activeMatch: {
    matchId: number;
    startedAt: string;
    earliestNewStart: string;
  } | null;
  scheduledStartTimes: string[];
  instantMatchBlockReason: string | null;
  scheduledMatchBlockReason: string | null;
}

// -- Constants -----------------------------------------------

const MATCH_CONSTANTS = {
  AVAILABLE_NOW_EXPIRY_MINUTES: 15,
  DIRECT_CHALLENGE_EXPIRY_MINUTES: 15,
  MAX_SCHEDULE_AHEAD_DAYS: 7,
  SAME_OPPONENT_COOLDOWN_HOURS: 3,
  MAX_MATCHES_PER_HOUR: 5,
  MAX_PENDING_INSTANT_MATCHES: 1,
  MAX_PENDING_SCHEDULED_MATCHES: 2,
  MAX_PENDING_TOTAL_MATCHES: 3,
  SCHEDULE_BUFFER_MINUTES: 60,
  SCHEDULE_INCREMENT_MINUTES: 15,
} as const;

const MATCH_TYPE_CONFIG: Array<{
  value: MatchType;
  label: string;
  description: string;
  icon: React.ReactElement;
}> = [
  {
    value: 'available_now',
    label: 'Available Now',
    description: 'Open challenge — any team can accept. Expires in 15 minutes if nobody takes it.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 6v8l5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    value: 'scheduled',
    label: 'Scheduled',
    description: 'Set a future time within the next 7 days. Any team can accept before the scheduled time.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="6" width="20" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M4 12h20M9 4v4M19 4v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="14" cy="18" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    value: 'direct_challenge',
    label: 'Direct Challenge',
    description: 'Challenge a specific team. They have 15 minutes to accept or decline.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14h14M21 14l-4-4M21 14l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5" cy="14" r="2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
];

const MATCH_FORMAT_CONFIG: Array<{
  value: MatchFormat;
  label: string;
  description: string;
}> = [
  { value: 'bo1', label: 'Bo1', description: 'Single game' },
  { value: 'bo3', label: 'Bo3', description: 'First to 2' },
  { value: 'bo5', label: 'Bo5', description: 'First to 3' },
  { value: 'bo7', label: 'Bo7', description: 'First to 4' },
];

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

// -- Helpers -------------------------------------------------

function roundToNext15(date: Date): Date {
  const d = new Date(date);
  const min = d.getMinutes();
  const remainder = min % 15;
  if (remainder !== 0) d.setMinutes(min + (15 - remainder));
  d.setSeconds(0, 0);
  return d;
}

function toLocalDatetimeString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getMinDate(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return toLocalDatetimeString(roundToNext15(now));
}

function getMaxDate(seasonEndDate?: string): string {
  const sevenDays = new Date();
  sevenDays.setDate(sevenDays.getDate() + MATCH_CONSTANTS.MAX_SCHEDULE_AHEAD_DAYS);
  if (seasonEndDate) {
    const seasonEnd = new Date(seasonEndDate);
    const cap = sevenDays < seasonEnd ? sevenDays : seasonEnd;
    return toLocalDatetimeString(cap);
  }
  return toLocalDatetimeString(sevenDays);
}

function hasScheduleConflict(scheduledAt: string, existingTimes: string[]): string | null {
  const target = new Date(scheduledAt).getTime();
  const bufferMs = MATCH_CONSTANTS.SCHEDULE_BUFFER_MINUTES * 60 * 1000;
  for (const existing of existingTimes) {
    const gap = Math.abs(target - new Date(existing).getTime());
    if (gap < bufferMs) {
      return `Too close to your other scheduled match at ${new Date(existing).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Must be at least 1 hour apart.`;
    }
  }
  return null;
}

function isOn15MinMark(dateStr: string): boolean {
  return new Date(dateStr).getMinutes() % 15 === 0;
}

// -- Component -----------------------------------------------

const CreateMatchPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { gameId, hasGame, season } = useGame();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // -- Team info state -------------------------------------
  const [userTeam, setUserTeam] = useState<UserTeamInfo | null>(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState<string | null>(null);

  // -- Match limits state ----------------------------------
  const [limits, setLimits] = useState<MatchLimits | null>(null);

  // -- Derive allowed formats from season ------------------
  const availableFormats = useMemo(() => {
    const allowed = season?.allowedFormats;
    if (!allowed || allowed.length === 0) {
      return MATCH_FORMAT_CONFIG.filter((f) => f.value !== 'bo1');
    }
    return MATCH_FORMAT_CONFIG.filter((f) => allowed.includes(f.value));
  }, [season?.allowedFormats]);

  // -- Form state ------------------------------------------
  const [matchType, setMatchType] = useState<MatchType>('available_now');
  const [matchFormat, setMatchFormat] = useState<MatchFormat>('bo3');
  const [scheduledAt, setScheduledAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // -- Reset format if current selection isn't allowed -----
  useEffect(() => {
    const allowedValues = availableFormats.map((f) => f.value);
    if (!allowedValues.includes(matchFormat) && allowedValues.length > 0) {
      setMatchFormat(allowedValues[0]);
    }
  }, [availableFormats, matchFormat]);

  // -- Team search state (direct challenge) ----------------
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AutocompleteTeam[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<AutocompleteTeam | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // -- Redirect if not authenticated -----------------------
  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/auth', { replace: true });
  }, [authLoading, isAuthenticated, navigate]);

  // -- Fetch user team info --------------------------------
  useEffect(() => {
    if (!gameId || !isAuthenticated) { setTeamLoading(false); return; }
    let cancelled = false;
    const fetchTeamInfo = async () => {
      try {
        setTeamLoading(true);
        setTeamError(null);
        const res = await axiosClient.get(`/dashboard?game_id=${gameId}`);
        if (cancelled) return;
        if (res.data?.success && res.data.data?.userTeam) {
          setUserTeam(res.data.data.userTeam);
        } else {
          setUserTeam(null);
        }
      } catch (err: any) {
        if (cancelled) return;
        setTeamError(err?.response?.data?.message || err?.response?.data?.error || 'Unable to load team info.');
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    };
    fetchTeamInfo();
    return () => { cancelled = true; };
  }, [gameId, isAuthenticated]);

  // -- Fetch match limits ----------------------------------
  useEffect(() => {
    if (!userTeam || userTeam.role !== 'captain') return;
    let cancelled = false;
    const fetchLimits = async () => {
      try {
        const res = await axiosClient.get(`/matches/limits?teamId=${userTeam.teamId}`);
        if (cancelled) return;
        if (res.data?.success) setLimits(res.data.data);
      } catch { /* Non-critical */ }
    };
    fetchLimits();
    return () => { cancelled = true; };
  }, [userTeam]);

  // -- Derive disabled match types from limits -------------
  const isTypeDisabled = useCallback((type: MatchType): boolean => {
    if (!limits) return false;
    if (limits.pendingTotalCount >= MATCH_CONSTANTS.MAX_PENDING_TOTAL_MATCHES) return true;
    if (type === 'available_now' || type === 'direct_challenge') return !limits.canCreateInstant;
    if (type === 'scheduled') return !limits.canCreateScheduled;
    return false;
  }, [limits]);

  const getTypeBlockReason = useCallback((type: MatchType): string | null => {
    if (!limits) return null;
    if (limits.pendingTotalCount >= MATCH_CONSTANTS.MAX_PENDING_TOTAL_MATCHES) {
      return `Maximum ${MATCH_CONSTANTS.MAX_PENDING_TOTAL_MATCHES} pending matches reached.`;
    }
    if (type === 'available_now' || type === 'direct_challenge') return limits.instantMatchBlockReason;
    if (type === 'scheduled') return limits.scheduledMatchBlockReason;
    return null;
  }, [limits]);

  // -- Auto-switch to available type if current is disabled -
  useEffect(() => {
    if (!limits) return;
    if (!isTypeDisabled(matchType)) return;
    const available = MATCH_TYPE_CONFIG.find((c) => !isTypeDisabled(c.value));
    if (available) setMatchType(available.value);
  }, [limits, matchType, isTypeDisabled]);

  // -- Team search -----------------------------------------
  const searchTeams = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) { setSearchResults([]); setShowDropdown(false); return; }
    try {
      setSearchLoading(true);
      const res = await axiosClient.get('/teams/autocomplete', {
        params: { q: query, limit: 10, game_id: gameId },
      });
      if (res.data?.success) { setSearchResults(res.data.data.teams || []); setShowDropdown(true); }
    } catch { setSearchResults([]); }
    finally { setSearchLoading(false); }
  }, [gameId]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFieldErrors((prev) => ({ ...prev, challengedTeamId: '' }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < MIN_SEARCH_LENGTH) { setSearchResults([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(() => searchTeams(value), DEBOUNCE_MS);
  }, [searchTeams]);

  const handleSelectTeam = useCallback((team: AutocompleteTeam) => {
    setSelectedTeam(team); setSearchQuery(''); setSearchResults([]); setShowDropdown(false);
    setFieldErrors((prev) => ({ ...prev, challengedTeamId: '' }));
  }, []);

  const handleClearTeam = useCallback(() => {
    setSelectedTeam(null); setSearchQuery('');
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          searchInputRef.current && !searchInputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setScheduledAt(''); setSelectedTeam(null); setSearchQuery('');
    setSearchResults([]); setShowDropdown(false); setFieldErrors({});
  }, [matchType]);

  // -- Form validation -------------------------------------
  const isFormValid = useMemo(() => {
    if (!userTeam || userTeam.role !== 'captain') return false;
    if (!matchType || !matchFormat) return false;
    if (isTypeDisabled(matchType)) return false;
    if (matchType === 'scheduled') {
      if (!scheduledAt) return false;
      if (!isOn15MinMark(scheduledAt)) return false;
      if (limits && hasScheduleConflict(scheduledAt, limits.scheduledStartTimes)) return false;
    }
    if (matchType === 'direct_challenge' && !selectedTeam) return false;
    return true;
  }, [userTeam, matchType, matchFormat, scheduledAt, selectedTeam, limits, isTypeDisabled]);

  // -- Submit handler --------------------------------------
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userTeam || !isFormValid) return;

    const errors: Record<string, string> = {};
    if (matchType === 'scheduled') {
      const scheduledDate = new Date(scheduledAt);
      const now = new Date();
      if (scheduledDate <= now) errors.scheduledAt = 'Scheduled time must be in the future.';
      const maxAhead = new Date();
      maxAhead.setDate(maxAhead.getDate() + MATCH_CONSTANTS.MAX_SCHEDULE_AHEAD_DAYS);
      if (scheduledDate > maxAhead) errors.scheduledAt = `Cannot schedule more than ${MATCH_CONSTANTS.MAX_SCHEDULE_AHEAD_DAYS} days ahead.`;
      if (!isOn15MinMark(scheduledAt)) errors.scheduledAt = 'Scheduled time must be on a 15-minute mark (e.g., 2:00, 2:15, 2:30, 2:45).';
      if (limits) {
        const conflict = hasScheduleConflict(scheduledAt, limits.scheduledStartTimes);
        if (conflict) errors.scheduledAt = conflict;
      }
      if (season?.endDate && scheduledDate > new Date(season.endDate)) errors.scheduledAt = 'Cannot schedule after the season ends.';
    }
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    try {
      setSubmitting(true);
      setFieldErrors({});
      const payload: CreateMatchPayload = { teamId: userTeam.teamId, matchType, matchFormat };
      if (matchType === 'scheduled') payload.scheduledAt = new Date(scheduledAt).toISOString();
      if (matchType === 'direct_challenge' && selectedTeam) payload.challengedTeamId = selectedTeam.teamId;

      const res = await axiosClient.post('/matches', payload);
      if (res.status === 201 || res.data?.matchId || res.data?.success) {
        const msg = matchType === 'direct_challenge' ? `Challenge sent to ${selectedTeam?.teamName}!`
          : matchType === 'scheduled' ? 'Your scheduled match is now posted.'
            : 'Your match is live — waiting for an opponent!';
        toastRef.current.success('Match Created', msg);
        // Brief delay so the toast is visible before navigating
        setTimeout(() => navigate('/matches'), 1200);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409 && data?.code) {
        const warnCodes = ['INSTANT_MATCH_LIMIT', 'SCHEDULED_MATCH_LIMIT', 'TOTAL_MATCH_LIMIT', 'ACTIVE_MATCH_WINDOW', 'SCHEDULE_BUFFER', 'OPPONENT_BUSY'];
        if (warnCodes.includes(data.code)) {
          toastRef.current.warning('Match Limit', data.message);
          try {
            const lr = await axiosClient.get(`/matches/limits?teamId=${userTeam.teamId}`);
            if (lr.data?.success) setLimits(lr.data.data);
          } catch { /* ignore */ }
          return;
        }
      }
      if (status === 400 && data?.code === 'SCHEDULE_INCREMENT') { setFieldErrors({ scheduledAt: data.message }); return; }
      if (status === 400 && data?.details) {
        const se: Record<string, string> = {};
        (data.details as ApiValidationError[]).forEach((d) => { se[d.field] = d.message; });
        setFieldErrors(se);
        toastRef.current.error('Validation Error', 'Please fix the errors below.');
      } else if (status === 403) {
        if (data?.missingIgns && Array.isArray(data.missingIgns)) {
          const names = data.missingIgns.map((m: any) => m.username || m).join(', ');
          toastRef.current.error('IGN Required', `These team members need to set their in-game name: ${names}`);
        } else {
          toastRef.current.error('Not Authorized', data?.message || 'You are not authorized to create this match.');
        }
      } else if (status === 429) {
        const msg = data?.cooldownEndsAt ? `Cooldown active. Try again after ${new Date(data.cooldownEndsAt).toLocaleTimeString()}.` : data?.message || 'Rate limit reached.';
        toastRef.current.warning('Slow Down', msg);
      } else {
        toastRef.current.error('Error', data?.message || data?.error || 'Something went wrong. Please try again.');
      }
    } finally { setSubmitting(false); }
  }, [userTeam, isFormValid, matchType, matchFormat, scheduledAt, selectedTeam, season, limits, navigate]);

  // -- Guard renders + Main render --------------------------

  if (!hasGame) {
    return (
      <div className="sl-cm-page"><div className="sl-cm-page-inner">
        <div className="sl-card sl-cm-guard">
          <h2 className="sl-section-title">No Game Selected</h2>
          <p className="sl-card-subtitle">Visit a game-specific subdomain to create a match.</p>
        </div>
      </div></div>
    );
  }

  if (authLoading || teamLoading) {
    return (
      <div className="sl-cm-page"><div className="sl-cm-page-inner">
        <SeasonHeader season={season} loading={true} />
        <div className="sl-card sl-cm-card">
          <div className="sl-cm-skeleton-header">
            <div className="sl-skeleton-pulse sl-skeleton-line--lg" style={{ width: '45%' }} />
            <div className="sl-skeleton-pulse sl-skeleton-line--md" style={{ width: '65%', marginTop: '0.5rem' }} />
          </div>
          <div className="sl-cm-skeleton-body">
            {[1, 2, 3].map((i) => (<div key={i} className="sl-skeleton-pulse sl-cm-skeleton-card" />))}
          </div>
        </div>
      </div></div>
    );
  }

  if (!userTeam) {
    return (
      <div className="sl-cm-page"><div className="sl-cm-page-inner">
        <SeasonHeader season={season} loading={false} />
        <div className="sl-card sl-cm-guard">
          <div className="sl-cm-guard-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M20 8c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12S26.627 8 20 8z" stroke="currentColor" strokeWidth="2" />
              <path d="M16 19h8M20 15v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="sl-section-title">You Need a Team</h2>
          <p className="sl-card-subtitle">You must be on a team before you can create a match. Create or join a team to get started.</p>
          <Button variant="primary" size="md" onClick={() => navigate('/create-team')} className="sl-cm-guard-btn">Create a Team</Button>
        </div>
      </div></div>
    );
  }

  if (userTeam.role !== 'captain') {
    return (
      <div className="sl-cm-page"><div className="sl-cm-page-inner">
        <SeasonHeader season={season} loading={false} />
        <div className="sl-card sl-cm-guard">
          <div className="sl-cm-guard-icon sl-cm-guard-icon--warning">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M20 14v6M20 24h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              <path d="M18.09 8.32L6.4 28.5a2 2 0 001.74 3h23.72a2 2 0 001.74-3L21.91 8.32a2 2 0 00-3.82 0z" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <h2 className="sl-section-title">Captain Required</h2>
          <p className="sl-card-subtitle">Only team captains can create matches. Ask your captain to create a match or transfer captaincy to you.</p>
          <Button variant="ghost" size="md" onClick={() => navigate('/')} className="sl-cm-guard-btn">Back to Dashboard</Button>
        </div>
      </div></div>
    );
  }

  if (teamError) {
    return (
      <div className="sl-cm-page"><div className="sl-cm-page-inner">
        <SeasonHeader season={season} loading={false} />
        <div className="sl-dashboard-error">
          <span>{teamError}</span>
          <button className="sl-dashboard-error-retry" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div></div>
    );
  }

  // -- Inline schedule warnings ----------------------------
  const scheduleConflictMsg = scheduledAt && limits ? hasScheduleConflict(scheduledAt, limits.scheduledStartTimes) : null;
  const scheduleIncrementError = scheduledAt && !isOn15MinMark(scheduledAt) ? 'Time must be on a 15-minute mark (e.g., 2:00, 2:15, 2:30, 2:45).' : null;

  return (
    <div className="sl-cm-page">
      <div className="sl-cm-page-inner">
        <SeasonHeader season={season} loading={false} />

        {/* Page title */}
        <div className="sl-cm-page-header">
          <button className="sl-cm-back-btn" onClick={() => navigate('/matches')} aria-label="Back to matches">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 15l-5-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div>
            <h1 className="sl-cm-page-title">Create Match</h1>
            <p className="sl-cm-page-subtitle">Set up a new challenge for your team</p>
          </div>
        </div>

        {/* Match slot summary */}
        {limits && (
          <div className="sl-cm-limits-bar">
            <span className="sl-cm-limits-item">Instant: {limits.pendingInstantCount}/{MATCH_CONSTANTS.MAX_PENDING_INSTANT_MATCHES}</span>
            <span className="sl-cm-limits-divider">&middot;</span>
            <span className="sl-cm-limits-item">Scheduled: {limits.pendingScheduledCount}/{MATCH_CONSTANTS.MAX_PENDING_SCHEDULED_MATCHES}</span>
            <span className="sl-cm-limits-divider">&middot;</span>
            <span className="sl-cm-limits-item">Total: {limits.pendingTotalCount}/{MATCH_CONSTANTS.MAX_PENDING_TOTAL_MATCHES}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="sl-cm-form">
          {/* Step 1: Match Type */}
          <div className="sl-card sl-cm-card">
            <div className="sl-cm-section-header">
              <span className="sl-cm-step-badge">1</span>
              <h2 className="sl-cm-section-title">Match Type</h2>
            </div>
            <p className="sl-cm-section-desc">Choose how you want to find an opponent.</p>

            <div className="sl-cm-type-grid">
              {MATCH_TYPE_CONFIG.map((config) => {
                const disabled = isTypeDisabled(config.value);
                const blockReason = getTypeBlockReason(config.value);
                return (
                  <button
                    key={config.value}
                    type="button"
                    className={`sl-cm-type-card ${matchType === config.value ? 'sl-cm-type-card--active' : ''} ${disabled ? 'sl-cm-type-card--disabled' : ''}`}
                    onClick={() => !disabled && setMatchType(config.value)}
                    aria-pressed={matchType === config.value}
                    disabled={disabled}
                    title={blockReason || undefined}
                  >
                    <div className="sl-cm-type-card-icon">{config.icon}</div>
                    <div className="sl-cm-type-card-content">
                      <span className="sl-cm-type-card-label">{config.label}</span>
                      {disabled && blockReason
                        ? <span className="sl-cm-type-card-blocked">{blockReason}</span>
                        : <span className="sl-cm-type-card-desc">{config.description}</span>}
                    </div>
                    {matchType === config.value && !disabled && (
                      <div className="sl-cm-type-card-check">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 8l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: Match Format */}
          <div className="sl-card sl-cm-card">
            <div className="sl-cm-section-header">
              <span className="sl-cm-step-badge">2</span>
              <h2 className="sl-cm-section-title">Match Format</h2>
            </div>
            <p className="sl-cm-section-desc">Game types and maps are auto-generated based on format.</p>
            <div className="sl-cm-format-group">
              {availableFormats.map((config) => (
                <button key={config.value} type="button"
                  className={`sl-cm-format-btn ${matchFormat === config.value ? 'sl-cm-format-btn--active' : ''}`}
                  onClick={() => setMatchFormat(config.value)} aria-pressed={matchFormat === config.value}>
                  <span className="sl-cm-format-btn-label">{config.label}</span>
                  <span className="sl-cm-format-btn-desc">{config.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 3: Scheduled */}
          {matchType === 'scheduled' && (
            <div className="sl-card sl-cm-card" style={{ animationDelay: '0.05s' }}>
              <div className="sl-cm-section-header">
                <span className="sl-cm-step-badge">3</span>
                <h2 className="sl-cm-section-title">Schedule Time</h2>
              </div>
              <p className="sl-cm-section-desc">
                Pick a date and time within the next {MATCH_CONSTANTS.MAX_SCHEDULE_AHEAD_DAYS} days.
                {season?.endDate && (<> Season ends {new Date(season.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.</>)}
              </p>
              <div className="sl-cm-schedule-field">
                <label htmlFor="cm-scheduled-at" className="sl-input-label">Date &amp; Time</label>
                <DateTimePicker
                  id="cm-scheduled-at"
                  value={scheduledAt}
                  minDate={getMinDate()}
                  maxDate={getMaxDate(season?.endDate)}
                  hasError={!!(fieldErrors.scheduledAt || scheduleConflictMsg || scheduleIncrementError)}
                  onChange={(val) => {
                    setScheduledAt(val);
                    setFieldErrors((prev) => ({ ...prev, scheduledAt: '' }));
                  }}
                />
                {fieldErrors.scheduledAt && <p className="sl-cm-field-error" role="alert">{fieldErrors.scheduledAt}</p>}
                {!fieldErrors.scheduledAt && scheduleIncrementError && <p className="sl-cm-field-error" role="alert">{scheduleIncrementError}</p>}
                {!fieldErrors.scheduledAt && !scheduleIncrementError && scheduleConflictMsg && <p className="sl-cm-field-error" role="alert">{scheduleConflictMsg}</p>}
              </div>
              <div className="sl-cm-info-banner">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 5v3M8 10h.005" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>The match will expire if no team accepts by the scheduled time. Matches cannot be reported until after the scheduled start time.</span>
              </div>
            </div>
          )}

          {/* Step 3: Direct Challenge */}
          {matchType === 'direct_challenge' && (
            <div className="sl-card sl-cm-card" style={{ animationDelay: '0.05s' }}>
              <div className="sl-cm-section-header">
                <span className="sl-cm-step-badge">3</span>
                <h2 className="sl-cm-section-title">Challenge a Team</h2>
              </div>
              <p className="sl-cm-section-desc">Search for the team you want to challenge. They'll have {MATCH_CONSTANTS.DIRECT_CHALLENGE_EXPIRY_MINUTES} minutes to respond.</p>

              {selectedTeam ? (
                <div className="sl-cm-selected-team">
                  <div className="sl-cm-selected-team-info">
                    <div className="sl-cm-team-logo-wrap">
                      {selectedTeam.logoUrl ? <img src={selectedTeam.logoUrl} alt={selectedTeam.teamName} className="sl-cm-team-logo" /> : <DefaultTeamIconSmall />}
                    </div>
                    <div className="sl-cm-selected-team-details">
                      <span className="sl-cm-selected-team-name">
                        {selectedTeam.teamName}
                        {selectedTeam.teamTag && <span className="sl-cm-selected-team-tag">[{selectedTeam.teamTag}]</span>}
                      </span>
                      <span className="sl-cm-selected-team-meta">{selectedTeam.rank ? `Rank #${selectedTeam.rank}` : 'Unranked'} &middot; {selectedTeam.points} pts</span>
                    </div>
                  </div>
                  <button type="button" className="sl-cm-clear-team-btn" onClick={handleClearTeam} aria-label="Clear selected team">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  </button>
                </div>
              ) : (
                <div className="sl-cm-search-wrap">
                  <div className="sl-cm-search-input-wrap">
                    <svg className="sl-cm-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <input ref={searchInputRef} type="text"
                      className={`sl-input sl-cm-search-input ${fieldErrors.challengedTeamId ? 'sl-input--error' : ''}`}
                      placeholder="Search teams by name or tag..." value={searchQuery}
                      onChange={handleSearchChange}
                      onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                      autoComplete="off" spellCheck="false" />
                    {searchLoading && (
                      <div className="sl-cm-search-spinner">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                          <path d="M8 2a6 6 0 015.92 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {fieldErrors.challengedTeamId && <p className="sl-cm-field-error" role="alert">{fieldErrors.challengedTeamId}</p>}
                  {showDropdown && (
                    <div ref={dropdownRef} className="sl-cm-search-dropdown">
                      {searchResults.length === 0 ? (
                        <div className="sl-cm-search-empty">No teams found for &ldquo;{searchQuery}&rdquo;</div>
                      ) : searchResults.map((team) => (
                        <button key={team.teamId} type="button" className="sl-cm-search-result" onClick={() => handleSelectTeam(team)}>
                          <div className="sl-cm-team-logo-wrap sl-cm-team-logo-wrap--sm">
                            {team.logoUrl ? <img src={team.logoUrl} alt={team.teamName} className="sl-cm-team-logo" /> : <DefaultTeamIconSmall />}
                          </div>
                          <div className="sl-cm-search-result-info">
                            <span className="sl-cm-search-result-name">{team.teamName}{team.teamTag && <span className="sl-cm-search-result-tag">[{team.teamTag}]</span>}</span>
                            <span className="sl-cm-search-result-meta">{team.rank ? `#${team.rank}` : 'Unranked'} &middot; {team.points} pts</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_LENGTH && (
                    <p className="sl-cm-search-hint">Type at least {MIN_SEARCH_LENGTH} characters to search</p>
                  )}
                </div>
              )}

              <div className="sl-cm-info-banner sl-cm-info-banner--warning">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 5v3M8 10h.005" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M7.09 1.82L1.4 12a1 1 0 00.87 1.5h11.38a1 1 0 00.87-1.5L8.91 1.82a1 1 0 00-1.82 0z" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <span>Both teams will be blocked from creating other matches while this challenge is pending.</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="sl-cm-footer">
            <Button type="button" variant="ghost" size="md" onClick={() => navigate('/matches')}>Cancel</Button>
            <Button type="submit" variant="primary" size="lg" disabled={!isFormValid || submitting} isLoading={submitting}>
              {submitting ? 'Creating...' : 'Create Match'}
            </Button>
          </div>
        </form>

        <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
      </div>
    </div>
  );
};

export default CreateMatchPage;